import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { Download } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { HemXBadge } from '../HemXBadge';
import { Modal } from '../Modal';
import { Touchable } from '../Touchable';
import * as styles from './Carousel.module.scss';
import { DotButton, NextButton, PrevButton } from './CarouselButtons';

type Props = {
  slides: JSX.Element[];
  thumbnails?: JSX.Element[];
  showThumbnails?: boolean;
  slideWidth?: string | number;
  alignItems?: 'start' | 'center' | 'end';
  loop?: boolean;
  startIndex?: number;
  slidesToScroll?: number;
  downloads?: readonly {
    readonly url: string;
    readonly gatsbyImageData: IGatsbyImageData;
  }[];
  isHemX?: boolean;
  gap?: boolean;
  align?: 'start' | 'center' | 'end';
  isSticky?: boolean;
  showPrevNext?: boolean;
};

export const Carousel: React.FC<Props> = ({
  slides,
  thumbnails = [],
  showThumbnails = true,
  slideWidth = 'auto',
  alignItems = 'center',
  loop = true,
  startIndex = 0,
  slidesToScroll = 1,
  downloads,
  isHemX = false,
  gap = true,
  align = 'center',
  isSticky = false,
  showPrevNext = true,
}): JSX.Element => {
  const [viewportRef, embla] = useEmblaCarousel({
    loop: loop,
    startIndex,
    slidesToScroll,
    align,
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
  const scrollTo = useCallback(
    (idx: number) => embla && embla.scrollTo(idx),
    [embla],
  );

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  const slideWidthStyle = {
    '--slide-width': slideWidth,
    '--slide-align-items': alignItems,
  } as React.CSSProperties;

  if (!slides) {
    return null;
  }

  return (
    <div
      className={classNames({
        'md:sticky md:top-14 md:h-fit': isSticky,
      })}
    >
      <div className={styles.wrapper} style={slideWidthStyle}>
        <div className={styles.viewport} ref={viewportRef}>
          <div className={classNames(styles.container, { [styles.gap]: gap })}>
            {React.Children.toArray(
              slides.map((slide, index) => (
                <div
                  className={styles.slide}
                  data-selected={index === selectedIndex}
                >
                  {slide}
                </div>
              )),
            )}
          </div>
        </div>
        {showPrevNext && (
          <>
            <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
            <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
          </>
        )}
        {isHemX ? <HemXBadge /> : null}
      </div>
      {showThumbnails ? (
        <div
          className={classNames({
            [styles.dots]: thumbnails.length === 0,
            [styles.thumbnails]: thumbnails.length > 0,
          })}
        >
          {thumbnails.length > 0
            ? React.Children.toArray(
                thumbnails.map((thumbnail, index) => (
                  <React.Fragment>
                    <Touchable
                      className={styles.thumb}
                      onClick={() => scrollTo(index)}
                      style={{ opacity: index === selectedIndex ? 1 : 0.5 }}
                    >
                      {thumbnail}
                    </Touchable>
                    <DotButton
                      selected={index === selectedIndex}
                      onClick={() => scrollTo(index)}
                      aria-label={`Scroll to image ${index + 1}`}
                    />
                  </React.Fragment>
                )),
              )
            : React.Children.toArray(
                scrollSnaps.map((_, index) => (
                  <DotButton
                    selected={index === selectedIndex}
                    onClick={() => scrollTo(index)}
                    aria-label={`Scroll to image ${index + 1}`}
                  />
                )),
              )}

          {!!downloads && (
            <>
              <Modal
                trigger={
                  <button
                    className={styles.dl}
                    title="Download high-res product images"
                  >
                    <Download size={20} className="text-blue" />
                  </button>
                }
                title="Download high-res product images"
                content={
                  <ul className="divide-y">
                    {thumbnails.map((thumbnail, index) => {
                      const { url } = downloads[index];

                      if (!url) {
                        return null;
                      }

                      return (
                        <li key={url}>
                          <a
                            className="grid grid-cols-[auto,auto,1fr,auto] items-center gap-2 pr-4 text-base hover:bg-black-50 hover:no-underline"
                            href={url}
                            target="_blank"
                            download
                          >
                            <span className="overflow-hidden rounded-sm">
                              {thumbnail}
                            </span>
                            <span className="text-base font-bold text-black">
                              Image {index + 1}
                            </span>
                            <FileExtension url={url} />
                            <span className="font-bold text-blue">
                              Download
                            </span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                }
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

const FileExtension = ({ url }: { url: string }): JSX.Element => {
  const extension = url.split('.').pop();

  if (!extension) return null;

  // if extension is longer than 4 characters, return empty element
  if (extension.length > 4) return <span />;

  return (
    <span className="text-base text-black-400">{extension.toUpperCase()}</span>
  );
};
